import dayjs, { type Dayjs } from 'dayjs';
import type { RegularAvailabilityByDate } from '@discover-data/stores/restaurant/model/types';
import startTimes from '@discover-data/stores/restaurant/model/session/startTimes';
import isSlotAvailable from '@discover-data/stores/restaurant/utils/slots/isSlotAvailable';
import { type AvailabilitySearchType } from '@discover-data/stores/restaurant/gql/getters/allAvailabilitySearch';

export default function getSlots(
  availabilityByDate: RegularAvailabilityByDate,
  filterDate: string,
  selectedDate: string | null,
  session: SessionType,
  startTime?: Dayjs
) {
  // day slots
  if (selectedDate || filterDate !== 'week') {
    const daySlots = availabilityByDate[selectedDate || filterDate];

    if (!daySlots) {
      return null;
    }

    return daySlots
      .map((slot) => ({ ...slot, hasMultipleTimes: false }))
      .sort((slotA, slotB) =>
        dayjs(`${slotA.date} ${slotA.time}`).isBefore(dayjs(`${slotB.date} ${slotB.time}`)) ? -1 : 1
      );
  }

  // week slots
  const time = (startTime || dayjs(startTimes[session])).format('HH:mm:ss');

  const weekSlots = Object.entries(availabilityByDate).map(([date, dateAvailability]) => {
    // unfetched (loading)
    if (dateAvailability === undefined) {
      return undefined;
    }

    // fetched but no Firebase data (allow fetching from graph API on demand)
    if (dateAvailability === null) {
      return {
        date,
        time,
        available: true,
        id: '0',
        open: true,
        session,
        hasMultipleTimes: false,
        dataStatus: null,
      };
    }

    // unavailable
    if (dateAvailability.length === 0) {
      return {
        date,
        time,
        available: false,
        id: '0',
        open: true,
        session,
        hasMultipleTimes: false,
        dataStatus: 'CACHED' as AvailabilitySearchType['dataStatus'],
      };
    }

    // available
    const firstSlot = dateAvailability[0];
    const availableSlots = dateAvailability.filter((regularAvailabilitySlot) =>
      isSlotAvailable(regularAvailabilitySlot)
    );
    const firstAvailableSlot = availableSlots[0];
    const slot = firstAvailableSlot || firstSlot;

    return {
      ...slot,
      hasMultipleTimes: true,
    };
  }, []);

  return weekSlots;
}
